import PlaceTypeModel       from '@/models/services/map/PlaceTypeModel';
import Form                 from '@widesk-ui/components/form/Form';
import { FormField }        from '@widesk-ui/components/form/Form';
import SelectPlaceType      from '@selects/map/SelectPlaceType';
import { useState }         from 'react';
import { useMemo }          from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import Button               from '@widesk-ui/components/Button';
import { getIdFromUrn }     from '@widesk-core/tools/identifier';
import PlaceImportModel     from '@/models/services/map/PlaceImportModel';
import FileUpload           from '@widesk-ui/components/form/FileUpload';
import InputUrl             from '@widesk-ui/components/form/InputUrl';
import Segmented            from '@widesk-ui/Segmented';
import CheckImportInput     from '@/components/CheckImportInput';
import { ImportSource }     from '@/components/CheckImportInput';
import ApiCollection        from '@widesk-core/models/ApiCollection';
import ImportTypeModel      from '@/models/services/map/ImportTypeModel';
import ImportModel          from '@/models/services/map/ImportModel';
import ImportUrlModel       from '@/models/services/map/ImportUrlModel';
import { message }          from '@widesk-ui/hooks/useMessage';
import Alert                from '@widesk-ui/components/Alert';
import ProjectModel         from '@models/map/ProjectModel';
import Progress             from '@widesk-ui/components/Progress';
import userMercureListener  from '@/services/userMercureListener';

const mimeTypeReferences = ['text/csv'];

const sourceOptions = [
	{ value: 'file', label: 'Depuis mon ordinateur' },
	{ value: 'url', label: 'Depuis une url' },
];

export default function ImportPlaceForm({ project }: {
	project: ProjectModel;
}) {
	const [placeTypeUrn, setPlaceTypeUrn] = useState<Urn | null>(null);
	const [sourceType, setSourceType] = useState<ImportSource['type']>('file');
	const [files, setFiles] = useState<File[]>([]);
	const [url, setUrl] = useState('');
	const [importProgress, setImportProgress] = useState<{ current: number, total: number }>();
	const importTypeCollection = useMemo(() => new ApiCollection(ImportTypeModel), []);

	if (!project?.id) {
		return null;
	}

	const source: ImportSource | null = sourceType === 'url'
		? (url ? { type: 'url', url } : null)
		: (files[0] ? { type: 'file', file: files[0] } : null);

	let fields: Record<string, FormField> = {
		message: {
			element: <Alert message={<ul>
				<li>Veuillez sélectionner le {PlaceTypeModel.localize('default', 'l')} que vous souhaitez importer puis télécharger le fichier modèle</li>
				<li>Compléter le fichier sur votre ordinateur puis importer-le</li>
				<li>Important : les colonnes du fichiers peuvent être différentes en fonction du {PlaceTypeModel.localize('default', 'l')} sélectionné</li>
			</ul>} />,
			span: 24,
		},
		placeType: {
			label: PlaceTypeModel.localize(),
			element: (
				<SelectPlaceType
					projectUrn={project.urn}
					filters={{
						project: project.id,
					}}
					onChange={value => setPlaceTypeUrn(value as Urn)}
				/>
			),
			span: 24,
		},
	};

	if (placeTypeUrn) {
		fields = {
			...fields,
			templateBtn: {
				label: 'Modèle d\'import',
				element: (
					<Button onClick={async () => {
						const model = new PlaceImportModel({
							id: getIdFromUrn(placeTypeUrn),
						});
						await model.download('place-import-template.csv');
					}}>
						<DownloadOutlined /> Télécharger le modèle
					</Button>
				),
				span: 24,
			},
			sourceType: {
				label: 'Origine du fichier',
				// La valeur est pilotée par le formulaire : on ne fait que la refléter dans l'état local
				initialValue: sourceType,
				element: (
					<Segmented
						block
						options={sourceOptions}
						onChange={value => setSourceType(value as ImportSource['type'])}
					/>
				),
				span: 24,
			},
			...(sourceType === 'url' ? {
				url: {
					label: 'Url du fichier CSV',
					element: <InputUrl placeholder="https://exemple.com/places.csv" onChange={value => setUrl(value as string)} />,
					rules: InputUrl.rules,
					span: 24,
					required: true,
				},
			} : {
				file: {
					label: 'Fichier CSV',
					element: (
						<FileUpload
							mimeTypeReferences={mimeTypeReferences}
							onChange={list => setFiles((list as { fileList: File[] }).fileList)}
						/>
					),
					span: 24,
					required: true,
				},
			}),
			progress: {
				hidden: !importProgress,
				label: 'Import en cours',
				element: (
					<Progress
						type="line"
						percent={importProgress?.total
							? Math.floor((importProgress.current / importProgress.total) * 100)
							: 0}
					/>
				),
				span: 24,
			},
		};
	}

	if (placeTypeUrn && source) {
		fields = {
			...fields,
			check: {
				span: 24,
				label: 'Analyse du fichier',
				element: <CheckImportInput
					source={source}
					projectUrn={project.urn}
					importType={importTypeCollection.first()?.urn}
					placeType={placeTypeUrn}
				/>,
				required: true,
			},
		};
	}

	return (
		<Form
			onDidMount={async () => {
				await importTypeCollection
					.setFilter('reference', 'place')
					.list();
			}}
			fields={fields}
			continueAfterSubmit={{
				onContinue: () => {
					setPlaceTypeUrn(null);
					setFiles([]);
					setUrl('');
				},
			}}
			onSubmit={async () => {
				const model = source?.type === 'url' ? new ImportUrlModel() : new ImportModel();
				if (source?.type === 'url') {
					await model.patch({
						project: project.urn,
						importType: importTypeCollection.first()?.urn,
						importInformations: { placeTypeUrn },
						url: source.url,
					});
				} else {
					await model.patchFormData({
						file: source?.file,
						project: project.urn,
						importType: importTypeCollection.first()?.urn,
						importInformations: JSON.stringify({ placeTypeUrn }),
					});
				}

				message.success('L\'import des données est en cours...');

				setImportProgress({ current: 0, total: 0 });

				// L'import réel tourne dans un consumer : on suit son avancement via le topic de l'utilisateur.
				userMercureListener.on('import_places_progress', response => {
					if (response.objectUrn !== model.urn) {
						return true;
					}

					setImportProgress({ current: response.data.current, total: response.data.total });

					if (response.data.current < response.data.total) {
						return true;
					}

					setImportProgress(undefined);

					return false;
				});
			}}
		/>
	);
}
